import { SpaceBetween } from '@risk-smart/themed-cloudscape-components';
import HelpPanel from '@risk-smart/themed-cloudscape-components/help-panel';
import { cn, StatusDot, Surface } from '@risksmart-app/atomic-ui';
import Button from '@risksmart-app/components/src/button';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import { useAmplitude } from '@risksmart-app/components/src/segment';
import { useTools } from '@risksmart-app/components/src/tools/useTools';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RiskScoringSettings } from 'src/blocks';
import type { ObjectToolbarConfig } from 'src/blocks/object-toolbar';
import { ObjectToolbar } from 'src/blocks/object-toolbar';
import ActionsButton from 'src/components/actions-button/ActionsButton';
import {
  useInsertRiskAssessmentResultConfig,
  useUpdateRiskAssessmentResultConfig,
} from 'src/hooks/mutations';
import { useGetRiskAssessmentResultConfigVersions } from 'src/hooks/queries';
import { PageLayout } from 'src/layouts';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import {
  useCreateResultNotification,
  useUpdateResultNotification,
} from '@/hooks/useMutationResultNotification';

import { RiskScoringVersionHistory } from '../blocks/risk-scoring-version-history';
import { DiscardDialog } from './dialogs/DiscardDialog';
import { EditImpactCategoryDialog } from './dialogs/EditImpactCategoryDialog';
import { EditLevelDialog } from './dialogs/EditLevelDialog';
import { EditMatrixCellDialog } from './dialogs/EditMatrixCellDialog';
import { SaveDialog } from './dialogs/SaveDialog';
import { buildLang, buildPreviewLang, buildVersionHistoryLang } from './lang';
import { LivePreviewPanel } from './LivePreviewPanel';
import { transformToApiConfig } from './transform';
import { useRiskScoringSettingsStore } from './useRiskScoringSettingsStore';
import { toVersionHistoryEntries } from './version-history';

// Chrome added to the measured content width: HelpPanel inline padding
// (20 + 24) + scrollbar allowance. Clamped to keep tiny configs usable and
// stop large ones swallowing the page.
const PREVIEW_PANEL_MIN_WIDTH = 480;
const PREVIEW_PANEL_MAX_WIDTH = 1000;
const PREVIEW_PANEL_CHROME = 64;

const clampPreviewPanelWidth = (contentWidth: number): number =>
  Math.min(
    Math.max(contentWidth + PREVIEW_PANEL_CHROME, PREVIEW_PANEL_MIN_WIDTH),
    PREVIEW_PANEL_MAX_WIDTH
  );

const getModeLabel = (
  isNewConfig: boolean,
  createsNewVersion: boolean
): 'new' | 'new_version' | 'update' =>
  isNewConfig ? 'new' : createsNewVersion ? 'new_version' : 'update';

const Page = () => {
  const { t } = useTranslation(['common'], {
    keyPrefix: 'riskScoringSettings',
  });
  const objectToolbarEnabled = useIsFeatureFlagEnabled('object_toolbar');
  const {
    state,
    actions,
    isLoading,
    id,
    originalTimestamp,
    areSettingsComplete,
    requiresNewVersion,
    resetToInitial,
    refetch,
    editingLevel,
    editingImpactCategory,
    editingMatrixCell,
    setEditingLevel,
    setEditingImpactCategory,
    setEditingMatrixCell,
    confirmEditLevel,
    confirmEditImpactCategory,
    confirmEditMatrixCell,
  } = useRiskScoringSettingsStore();
  const { mutate: updateConfig } = useUpdateRiskAssessmentResultConfig();
  const { mutate: insertConfig } = useInsertRiskAssessmentResultConfig();
  const { addNotification } = useNotifications();
  const amplitude = useAmplitude();
  const [, setToolsContent] = useTools();

  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [panelWidth, setPanelWidth] = useState(PREVIEW_PANEL_MIN_WIDTH);

  const handlePreviewWidthChange = useCallback((contentWidth: number) => {
    setPanelWidth(clampPreviewPanelWidth(contentWidth));
  }, []);

  const isNewConfig = id === null;

  const {
    versions,
    loading: versionsLoading,
    refetch: refetchVersions,
  } = useGetRiskAssessmentResultConfigVersions({
    type: 'risk_assessment',
    skip: isNewConfig,
  });

  const versionHistoryEntries = useMemo(
    () => toVersionHistoryEntries(versions),
    [versions]
  );

  const hasLoggedViewRef = useRef(false);
  useEffect(() => {
    if (!isLoading && state && !hasLoggedViewRef.current) {
      hasLoggedViewRef.current = true;
      amplitude.logEvent('Risk Scoring Settings Viewed', {
        ...(id !== null && { scoring_settings_id: id }),
        methodology: state.selectedMethodology,
      });
    }
  }, [isLoading, state, id, amplitude]);

  const config = useMemo(
    () =>
      transformToApiConfig({
        likelihoodLevels: state.likelihoodLevels,
        impactLevels: state.impactLevels,
        impactCategories: state.impactCategories,
        impactAggregation: state.impactAggregation,
        matrix: state.matrix,
      }),
    [
      state.likelihoodLevels,
      state.impactLevels,
      state.impactCategories,
      state.impactAggregation,
      state.matrix,
    ]
  );

  const updateAction = useUpdateResultNotification<void>({
    entityName: t('entity_name'),
    asyncAction: async () => {
      const modeLabel = getModeLabel(false, false);
      try {
        await updateConfig({
          variables: {
            Id: id!,
            Config: config,
            OriginalTimestamp: originalTimestamp!,
          },
        });
        amplitude.logEvent('Risk Scoring Settings Saved', {
          methodology: state.selectedMethodology,
          mode: modeLabel,
          scoring_settings_id: id,
        });
        await refetch();
        await refetchVersions();

        return true;
      } catch (error) {
        amplitude.logEvent('Risk Scoring Settings Save Failed', {
          methodology: state.selectedMethodology,
          mode: modeLabel,
          ...(id !== null && { scoring_settings_id: id }),
        });
        throw error;
      }
    },
  });

  const insertAction = useCreateResultNotification<void>({
    entityName: t('entity_name'),
    asyncAction: async () => {
      const modeLabel = getModeLabel(isNewConfig, true);
      try {
        const insertResult = await insertConfig({
          variables: { Config: config, Type: 'risk_assessment' },
        });
        const newId = insertResult.insertRiskAssessmentResultConfigApi?.Id;
        amplitude.logEvent('Risk Scoring Settings Saved', {
          methodology: state.selectedMethodology,
          mode: modeLabel,
          ...(newId && { scoring_settings_id: newId }),
        });
        await refetch();
        await refetchVersions();

        return true;
      } catch (error) {
        amplitude.logEvent('Risk Scoring Settings Save Failed', {
          methodology: state.selectedMethodology,
          mode: modeLabel,
          ...(id !== null && { scoring_settings_id: id }),
        });
        throw error;
      }
    },
  });

  const handleSave = (mode: 'update' | 'insert') => {
    const effectiveMode = isNewConfig ? 'insert' : mode;

    if (effectiveMode === 'update') {
      void updateAction();
    } else {
      void insertAction();
    }

    setShowSaveDialog(false);
  };

  const handleDiscard = () => {
    amplitude.logEvent('Risk Scoring Settings Discarded', {
      methodology: state.selectedMethodology,
      mode: getModeLabel(isNewConfig, requiresNewVersion),
      ...(id !== null && { scoring_settings_id: id }),
    });

    setShowDiscardDialog(false);
    resetToInitial();
    addNotification({
      type: 'success',
      content: t('discardDialog.successMessage'),
    });
  };

  const existingLevelsForDialog = useMemo(() => {
    if (!editingLevel) {
      return [];
    }
    const levels =
      editingLevel.type === 'likelihood'
        ? state.likelihoodLevels
        : state.impactLevels;

    return levels.filter((l) => l.value !== editingLevel.level.value);
  }, [editingLevel, state.likelihoodLevels, state.impactLevels]);

  const existingCategoriesForDialog = useMemo(
    () =>
      editingImpactCategory
        ? state.impactCategories.filter(
            (c) => c.name !== editingImpactCategory.name
          )
        : [],
    [editingImpactCategory, state.impactCategories]
  );

  const lang = buildLang(t);
  const versionHistoryLang = buildVersionHistoryLang(t);
  const previewLang = buildPreviewLang(t);

  // Object-level toolbar (CUS-175) — flag-gated. Replaces the PageLayout header
  // with the object toolbar. This page's save flow is dialog-driven (there is no
  // bridged object form), so Save is surfaced as a primary action and the other
  // header controls (Discard, version history, live preview) move into the
  // overflow menu. Legacy `actions` below stay as the flag-off fallback.
  const toolbarConfig: ObjectToolbarConfig = useMemo(
    () => ({
      header: { title: t('page.title') },
      actions: {
        primary: [
          {
            id: 'save',
            label: t('page.saveButton'),
            iconName: 'save-01',
            onClick: () => setShowSaveDialog(true),
            disabled:
              state.changeStatus === 'none' ||
              isLoading ||
              !areSettingsComplete,
          },
        ],
        overflow: [
          {
            id: 'actions',
            actions: [
              {
                id: 'discard',
                label: t('page.discardButton'),
                onClick: () => setShowDiscardDialog(true),
                disabled: state.changeStatus === 'none' || isLoading,
              },
              // The legacy ActionsButton item carries a `disabledReason`
              // tooltip; `ToolbarAction` has no equivalent field and extending
              // the shared toolbar contract is out of scope for this migration,
              // so the explanation is folded into the menu label while the
              // action is unavailable rather than being dropped.
              {
                id: 'view-history',
                label:
                  isNewConfig || versionsLoading
                    ? t('versionHistory.viewHistoryDisabledLabel')
                    : t('versionHistory.viewHistory'),
                onClick: () => setShowVersionHistory(true),
                disabled: isNewConfig || versionsLoading,
              },
              {
                id: 'show-preview',
                label: t('preview.showPreview'),
                onClick: () => setToolsContent('page-content'),
              },
            ],
          },
        ],
      },
    }),
    [
      t,
      state.changeStatus,
      isLoading,
      areSettingsComplete,
      isNewConfig,
      versionsLoading,
      setToolsContent,
    ]
  );

  if (isLoading) {
    return (
      <PageLayout
        title={t('page.title')}
        headerOverride={
          objectToolbarEnabled ? (
            <ObjectToolbar config={{ header: { title: t('page.title') } }} />
          ) : undefined
        }
      />
    );
  }

  return (
    <PageLayout
      title={t('page.title')}
      headerOverride={
        objectToolbarEnabled ? (
          <ObjectToolbar config={toolbarConfig} />
        ) : undefined
      }
      panelContent={
        <HelpPanel
          header={
            <div className={cn('flex items-center gap-2')}>
              <StatusDot />
              {t('preview.title')}
            </div>
          }
        >
          <LivePreviewPanel
            config={config}
            lang={previewLang}
            onContentWidthChange={handlePreviewWidthChange}
          />
        </HelpPanel>
      }
      panelWidth={panelWidth}
      actions={
        <SpaceBetween direction={'horizontal'} size={'xs'}>
          <Button
            variant={'primary'}
            formAction={'none'}
            onClick={() => setShowSaveDialog(true)}
            disabled={
              state.changeStatus === 'none' || isLoading || !areSettingsComplete
            }
          >
            {t('page.saveButton')}
          </Button>
          <Button
            variant={'normal'}
            formAction={'none'}
            onClick={() => setShowDiscardDialog(true)}
            disabled={state.changeStatus === 'none' || isLoading}
          >
            {t('page.discardButton')}
          </Button>
          <ActionsButton
            buttonText={t('page.actionsButton')}
            testId={'scoringSettingsActions'}
            items={[
              {
                text: t('versionHistory.viewHistory'),
                id: 'viewHistory',
                disabled: isNewConfig || versionsLoading,
                disabledReason: t('versionHistory.viewHistoryDisabledReason'),
                onItemClick: () => setShowVersionHistory(true),
              },
              {
                text: t('preview.showPreview'),
                id: 'showPreview',
                onItemClick: () => setToolsContent('page-content'),
              },
            ]}
          />
        </SpaceBetween>
      }
    >
      <Surface padding={'lg'} shadow={'sm'} className={'mx-auto'}>
        <RiskScoringSettings lang={lang} state={state} actions={actions} />
      </Surface>

      <RiskScoringVersionHistory
        lang={versionHistoryLang}
        state={{ open: showVersionHistory, versions: versionHistoryEntries }}
        actions={{ onOpenChange: setShowVersionHistory }}
      />

      <SaveDialog
        open={showSaveDialog}
        onOpenChange={setShowSaveDialog}
        onUpdate={() => handleSave('update')}
        onInsert={() => handleSave('insert')}
        requiresNewVersion={isNewConfig || requiresNewVersion}
      />
      <DiscardDialog
        open={showDiscardDialog}
        onOpenChange={setShowDiscardDialog}
        onDiscard={handleDiscard}
      />
      <EditImpactCategoryDialog
        open={editingImpactCategory !== null}
        onOpenChange={(open) => {
          if (!open) {
            setEditingImpactCategory(null);
          }
        }}
        category={editingImpactCategory}
        onSave={confirmEditImpactCategory}
        existingCategories={existingCategoriesForDialog}
      />
      <EditMatrixCellDialog
        open={editingMatrixCell !== null}
        onOpenChange={(open) => {
          if (!open) {
            setEditingMatrixCell(null);
          }
        }}
        cell={editingMatrixCell}
        onSave={confirmEditMatrixCell}
      />
      <EditLevelDialog
        open={editingLevel !== null}
        onOpenChange={(open) => {
          if (!open) {
            setEditingLevel(null);
          }
        }}
        level={editingLevel?.level ?? null}
        type={editingLevel?.type ?? 'impact'}
        onSave={confirmEditLevel}
        existingLevels={existingLevelsForDialog}
      />
    </PageLayout>
  );
};

export default Page;
